/**
 * Pricing Settings Page - Preisverwaltung
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Euro as EuroIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import {
  PriceStructure,
  CreatePriceStructureRequest,
  UpdatePriceStructureRequest,
} from "../../types/api-types";
import LoadingSpinner from "../../components/Common/LoadingSpinner";

const PricingSettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [prices, setPrices] = useState<PriceStructure[]>([]);
  const [priceStructures, setPriceStructures] = useState<PriceStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<PriceStructure | null>(
    null
  );
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    price: PriceStructure | null;
  }>({
    open: false,
    price: null,
  });

  // Load pricing data
  const loadPricingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [priceList, structureList] = await Promise.all([
        apiClient.getPriceStructures(),
        apiClient.getPriceStructures(),
      ]);

      setPrices(priceList);
      setPriceStructures(structureList);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Preisdaten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricingData();
  }, []);

  const handleEditPrice = (price: PriceStructure) => {
    setSelectedPrice(price);
    setShowForm(true);
  };

  const handleAddPrice = () => {
    setSelectedPrice(null);
    setShowForm(true);
  };

  const handleDeletePrice = (price: PriceStructure) => {
    setDeleteDialog({ open: true, price });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.price) return;

    try {
      await apiClient.deletePriceStructure(deleteDialog.price.id);
      setPrices(prices.filter((p) => p.id !== deleteDialog.price!.id));
      setDeleteDialog({ open: false, price: null });
    } catch (err: any) {
      setError(err.message || "Fehler beim Löschen der Preisstruktur");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  if (loading) {
    return <LoadingSpinner text="Lade Preisdaten..." />;
  }

  if (showForm) {
    return (
      <PriceForm
        price={selectedPrice}
        onSave={async (priceData) => {
          try {
            setSaving(true);
            if (selectedPrice) {
              await apiClient.updatePriceStructure(selectedPrice.id, priceData);
            } else {
              await apiClient.createPriceStructure(priceData);
            }
            await loadPricingData();
            setShowForm(false);
            setSelectedPrice(null);
          } catch (err: any) {
            setError(err.message || "Fehler beim Speichern der Preisstruktur");
          } finally {
            setSaving(false);
          }
        }}
        onCancel={() => {
          setShowForm(false);
          setSelectedPrice(null);
        }}
        saving={saving}
      />
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <EuroIcon fontSize={isMobile ? "medium" : "large"} />
              Preisverwaltung
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Verwalten Sie Preise und Preisstrukturen für Mietobjekte
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPrice}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              borderRadius: 2,
            }}
          >
            Neuer Preis
          </Button>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Price Structures Overview */}
      {priceStructures.length > 0 && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            color="primary"
          >
            Preisstrukturen
          </Typography>
          <Grid container spacing={2}>
            {priceStructures.map((structure) => (
              <Grid item xs={12} sm={6} md={4} key={structure.id}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {structure.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {structure.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Object Prices */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
          Objektpreise
        </Typography>

        {prices.length === 0 ? (
          <Box
            sx={{
              p: { xs: 3, md: 6 },
              textAlign: "center",
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
              borderRadius: 2,
            }}
          >
            <EuroIcon
              sx={{
                fontSize: { xs: 60, md: 80 },
                color: "text.secondary",
                mb: 2,
              }}
            />
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              fontWeight="bold"
            >
              Keine Preise gefunden
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Erstellen Sie Ihren ersten Preis, um mit der Preisverwaltung zu
              beginnen.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddPrice}
              sx={{
                mt: 3,
                borderRadius: 2,
                px: 4,
                py: 1.5,
              }}
            >
              Ersten Preis anlegen
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {prices.map((price) => (
              <Grid item xs={12} md={6} lg={4} key={price.id}>
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: { xs: "none", md: "translateY(-4px)" },
                      boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
                      <Typography variant="h6" fontWeight="bold">
                        {price.name}
                      </Typography>
                    </Box>

                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {price.description}
                      </Typography>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          color="primary"
                        >
                          {formatCurrency(price.hourlyRate)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          pro Stunde
                        </Typography>
                      </Box>

                      {price.objectType && (
                        <Chip
                          label={price.objectType}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Stack>
                  </CardContent>

                  <Divider />

                  <CardActions
                    sx={{
                      p: { xs: 2, md: 3 },
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditPrice(price)}
                      variant="outlined"
                    >
                      Bearbeiten
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePrice(price)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, price: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Preis löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Preis "{deleteDialog.price?.name}" wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, price: null })}>
            Abbrechen
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Price Form Component
interface PriceFormProps {
  price: PriceStructure | null;
  onSave: (price: any) => void;
  onCancel: () => void;
  saving: boolean;
}

const PriceForm: React.FC<PriceFormProps> = ({
  price,
  onSave,
  onCancel,
  saving,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    name: price?.name || "",
    description: price?.description || "",
    hourlyRate: price?.hourlyRate?.toString() || "",
    objectType: price?.objectType || "",
    isGlobalForType: price?.isGlobalForType || false,
    isActive: price?.isActive !== false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const priceData = {
      ...formData,
      hourlyRate: parseFloat(formData.hourlyRate) || 0,
    };
    onSave(priceData);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <EuroIcon fontSize={isMobile ? "medium" : "large"} />
              {price ? "Preis bearbeiten" : "Neuer Preis"}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Preisname"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Objekttyp"
                  value={formData.objectType}
                  onChange={(e) =>
                    setFormData({ ...formData, objectType: e.target.value })
                  }
                  variant="outlined"
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="">Alle Typen</option>
                  <option value="TRAILER">Anhänger</option>
                  <option value="TOOL">Werkzeug</option>
                  <option value="VEHICLE">Fahrzeug</option>
                  <option value="EQUIPMENT">Ausrüstung</option>
                  <option value="MACHINE">Maschine</option>
                  <option value="HEBEBUEHNE">Hebebühne</option>
                  <option value="ARBEITSPLATZ">Arbeitsplatz</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Beschreibung"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Stundenpreis (€)"
                  type="number"
                  value={formData.hourlyRate}
                  onChange={(e) =>
                    setFormData({ ...formData, hourlyRate: e.target.value })
                  }
                  required
                  variant="outlined"
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
            </Grid>
          </form>
        </CardContent>

        <Divider />

        <CardActions
          sx={{ p: { xs: 2, md: 3 }, justifyContent: "flex-end", gap: 2 }}
        >
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={saving}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Speichere..." : "Speichern"}
          </Button>
        </CardActions>
      </Card>
    </Container>
  );
};

export default PricingSettingsPage;
